import type { StudentAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/StudentAssignmentCubit'
import { MAX_GROUP_NAME_LENGTH } from '@breakoutlearning/firebase-repository/constants'
import { BreakoutTextInput } from 'components/design-system/BreakoutTextInput'
import type { DateTime } from 'luxon'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useId, useMemo } from 'react'
import { BreakoutDateTimeInput } from 'components/design-system/BreakoutDateTimeInput'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { z } from 'zod'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { type RoomState } from '@breakoutlearning/firebase-repository/models/RoomState'
import { CircleAlert } from 'components/icons/CircleAlert'
import { Dialog } from 'components/dialogs/Dialog'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { useDialogs } from 'hooks/dialogs'
import { getRoomStateScheduledAtSchema } from '../roomStateScheduledAtSchema'
import { when } from 'mobx'
import { useTranslationTyped } from 'i18n/i18n'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { Asterisk } from 'components/design-system/form/Asterisk'

export const RoomStateFormV2DialogBody = observer(function RoomStateForm({
  room,
  defaultScheduledAt,
  cubit,
  onSave,
  virtualName,
  closeSelf,
  headingId,
}: {
  room: RoomState
  defaultScheduledAt: DateTime | undefined
  cubit: StudentAssignmentCubit
  onSave?: () => void
  virtualName?: string
  closeSelf: () => void
  headingId: string
}) {
  const minScheduleDate = cubit.effectiveMinScheduleDate
  const maxFormatted = cubit.effectiveExpiresAtDate

  const { tt } = useTranslationTyped()

  useEffect(() => {
    if (room.id) cubit.logEvent('show_create_group')
  }, [cubit, room.id])

  const getSchema = useCallback(
    (
      tt: ReturnType<typeof useTranslationTyped>['tt'],
      room: RoomState,
      rooms: RoomState[],
      expiresAt: DateTime | undefined,
      availableFrom: DateTime | undefined
    ) =>
      z.object({
        groupName: z
          .preprocess(
            (s) => {
              if (typeof s === 'string') {
                return s.trim()
              }
              return s
            },
            z
              .string()
              .min(1, tt.student_assignment.group_name_required())
              .max(
                MAX_GROUP_NAME_LENGTH,
                tt.student_assignment.group_name_too_long({
                  maxLength: MAX_GROUP_NAME_LENGTH,
                })
              )
          )
          .superRefine((value, ctx) => {
            // Check if the group name is unique
            const isInvalidName = rooms.some((r) => {
              // if the current room, allow the name
              if (r.id === room.id) return false
              // if conflicting name, return true (is invalid)
              return r.data.roomStateName === value
            })

            if (isInvalidName) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: tt.student_assignment.group_name_exists(),
              })
            }

            return true
          }),
        scheduledAt: getRoomStateScheduledAtSchema({
          tt,
          expiresAt,
          skipRequired: true, // NOTE(danielmartin0): An alternative would be to enforce keeping a date if the room already has one, but that is not what we do
          availableFrom,
        }),
      }),
    []
  )

  const schema = useMemo(
    () =>
      getSchema(
        tt,
        room,
        cubit.roomStates.models,
        cubit.effectiveExpiresAtDate,
        cubit.assignment.assignedAtDate
      ),
    [
      getSchema,
      tt,
      room,
      cubit.roomStates,
      cubit.effectiveExpiresAtDate,
      cubit.assignment.assignedAtDate,
    ]
  )
  type FormValues = z.infer<ReturnType<typeof getSchema>>

  const {
    control,
    formState: { isSubmitting, isValid },
    handleSubmit,
    trigger,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      groupName: room.data.roomStateName ?? virtualName ?? '',
      scheduledAt: defaultScheduledAt,
    },
    reValidateMode: 'onChange',
  })
  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      handleSubmit(async (data) => {
        const { groupName, scheduledAt } = data

        // if we are in process of editing a room data but are not part of the room or on the room's page as instructor
        // then let's join the room as group leader (we can not edit unless we are group leader)
        if (!cubit.roomStateId && room.id) {
          await cubit.joinRoom(room.id, { isGroupLeader: true })
          await when(() => !!cubit.roomStateId) //wait for roomStateId update (this should always happen after join)
        }

        try {
          await cubit.updateRoom(groupName, scheduledAt)
          onSave?.()
        } catch (e) {
          console.error(e)
        }
      })(e)
    },
    [cubit, handleSubmit, onSave, room.id]
  )

  const isEditing = useMemo(() => {
    // trigger one validation on mount if we are editing
    if (room.id) trigger()
    return !!room.id
  }, [room.id, trigger])

  return (
    <div className="flex h-full w-full justify-center">
      <form
        className="flex h-full w-full max-w-[450px] flex-col justify-center gap-8"
        onSubmit={onSubmit}
      >
        <div className="flex flex-col gap-1 text-center">
          <h1 id={headingId} className="text-headline-large">
            {!isEditing
              ? tt.student_assignment.join_dialog_v2.first_one_here()
              : tt.student_assignment.join_dialog_v2.edit_group()}
          </h1>
          <h2 className="text-body-large">
            {tt.student_assignment.join_dialog_v2.name_your_group()}
          </h2>
          <p className="text-body-large">
            <Asterisk />{' '}
            {tt.student_assignment.join_dialog_v2.required_fields()}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <Controller
            control={control}
            name="groupName"
            render={({ field, fieldState }) => (
              <BreakoutTextInput
                {...field}
                required
                onChange={(v) => {
                  field.onChange(v)
                  trigger('groupName')
                }}
                autoFocus
                kind="secondary"
                disabled={isSubmitting}
                error={fieldState.error}
                testId="room-state-form-name"
                label={tt.student_assignment.group_name()}
                name="groupName"
                placeholder={tt.student_assignment.group_name_placeholder()}
              />
            )}
          />
          <Controller
            control={control}
            name="scheduledAt"
            render={({ field, fieldState }) => {
              const disabledByInstructor =
                cubit.assignment.data.forbidStudentGroupScheduling
              const disabledByCompletion = !!cubit.roomGraded.value
              const isDisabled =
                isSubmitting || disabledByInstructor || disabledByCompletion
              const tooltipContent = disabledByInstructor
                ? tt.student_assignment.schedule_group_disabled_by_instructor()
                : disabledByCompletion
                  ? tt.student_assignment.schedule_group_disabled_completed()
                  : ''

              // Don't show validation errors for disabled fields
              const error =
                disabledByInstructor || disabledByCompletion
                  ? undefined
                  : fieldState.error

              return (
                <BreakoutTooltip
                  enabled={disabledByInstructor || disabledByCompletion}
                  content={tooltipContent}
                >
                  <div>
                    <BreakoutDateTimeInput
                      {...field}
                      error={error}
                      value={field.value}
                      kind="secondary"
                      disabled={isDisabled}
                      label={tt.student_assignment.meeting_time()}
                      min={minScheduleDate}
                      max={maxFormatted}
                      id="meeting-time"
                      name="scheduledAt"
                      onChange={(v) => {
                        field.onChange(v)
                        trigger()
                      }}
                      resolution="15min"
                    />
                  </div>
                </BreakoutTooltip>
              )
            }}
          />
          <div className="flex flex-row gap-1 pl-4">
            <CircleAlert size={10} className="stroke-on-surface-var" />
            <strong className="text-body-small text-on-surface-var">
              {tt.student_assignment.join_dialog_v2.meeting_date_info()}
            </strong>
          </div>
        </div>
        <div className="flex flex-row justify-between">
          <BreakoutButton
            data-testid="room-state-form-cancel"
            size="large"
            kind="secondary"
            onClick={closeSelf}
          >
            {tt.student_assignment.cancel()}
          </BreakoutButton>
          <BreakoutButton
            data-testid="room-state-form-confirm"
            size="large"
            kind={!isValid ? 'secondary' : 'accent'}
            loading={isSubmitting}
            disabled={!isValid}
            type="submit"
            loadingText={tt.common.confirm()}
          >
            {tt.common.confirm()}
          </BreakoutButton>
        </div>
      </form>
    </div>
  )
})

export const RoomStateFormDialogV2 = ({
  cubit,
  onSave,
}: {
  cubit: StudentAssignmentCubit
  onSave?: () => void
}) => {
  const { popDialog } = useDialogs()
  const headingId = useId()

  const defaultScheduledAt = useMemo(() => {
    return cubit.libraryObject.scheduledAtDate
  }, [cubit.libraryObject.scheduledAtDate])

  return (
    <Dialog
      testId="rooms-dialog"
      size="xl"
      className="w-[1200px]"
      innerClassName="flex flex-col justify-center h-full max-w-[1000px] mx-auto"
      aria-labelledby={headingId}
    >
      <DialogCloseButton />
      <RoomStateFormV2DialogBody
        closeSelf={() => popDialog()}
        room={cubit.roomState}
        defaultScheduledAt={defaultScheduledAt}
        onSave={onSave}
        cubit={cubit}
        headingId={headingId}
      />
    </Dialog>
  )
}
